import React from 'react';
import DateTimePicker from 'react-datetime-picker';
import 'react-datetime-picker/dist/DateTimePicker.css';
import 'react-calendar/dist/Calendar.css';

/**
 * Modal time selector that is used to set start and stop times for plot.
 *
 * @param props.startTime Datetime string that indicates the start time of interest.
 * @param props.stopTime Datetime string that indicates the stop time of interest.
 * @param prop.onStartTimeChange Function that is called when startTime value changes.
 * @param prop.onStopTimeChange Function that is called when stopTIme value changes.
 * @return JSX
 */
export function CdaWebModalTimeSelector(props) {
  const {timelineInfo, startTime, stopTime, onStartTimeChange, onStopTimeChange} = props;

  const simulateTabKey = () => {

    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    );

    const currentIndex = focusableElements.findIndex((el) => el === document.activeElement);
    const nextIndex = (currentIndex + 1) % focusableElements.length;
    focusableElements[nextIndex]?.focus();
  };

  /*const cleanTimeInput = (value) => {
        const originalValue = getUTCTimeString(value)
        let [date, time] = originalValue.split('T');
        let [year, month, day] = date.split('-');
        let [hours, minutes, seconds] = time.split(':');
        let shouldTab = false

        const timePadMap = {"year": [4, year], "month": [2, month], "day": [2, day], "hours": [2, hours], "minutes": [2, minutes], "seconds": [2, seconds]}

        for (const key in timePadMap) {
            const value = timePadMap[key];

            if(value[1].length > value[0]){
                value[1] = value[1].slice(0, 4);
                shouldTab = true
            }
        }

        if(shouldTab) {
            simulateTabKey();
        }

        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }*/

  const cleanTimeInput = (value) => {
      let originalValue = getUTCTimeString(value);

      // Defensive strip
      originalValue = originalValue
          .replace(/\.\d{1,3}(Z)?$/, '')
          .replace(/Z$/, '')
          .trim();

      let [date, time] = originalValue.split('T');
      if (!time) return originalValue;

      let [year, month, day] = date.split('-');
      let [hours, minutes, seconds] = time.split(':');

      // Handle possible extra junk in seconds
      seconds = seconds ? seconds.split('.')[0] || '00' : '00';

      const pad = (str, len) => str.padStart(len, '0').slice(0, len);

      year   = pad(year,   4);
      month  = pad(month,  2);
      day    = pad(day,    2);
      hours  = pad(hours,  2);
      minutes = pad(minutes, 2);
      seconds = pad(seconds, 2);

      const cleaned = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

      // Your tab logic...
      let shouldTab = false;


      if (shouldTab) {
          simulateTabKey();
      }

      return cleaned;
  };

  const passToStartTimeChange = (value) => {
        const cleanValue = cleanTimeInput(value);
        onStartTimeChange(cleanValue)
  }

  const passToStopTimeChange = (value) => {
        const cleanValue = cleanTimeInput(value);
        onStopTimeChange(cleanValue)
  }

  const padDateTime = (dateTime) => {
    let [date, time] = dateTime.split('T');
    let [year, month, day] = date.split('-');
    let [hours, minutes, seconds] = time.split(':');

    const timePadMap = {"year": [4, year], "month": [2, month], "day": [2, day], "hours": [2, hours], "minutes": [2, minutes], "seconds": [2, seconds]}

    for (const key in timePadMap) {
        const value = timePadMap[key];

        if(value[1].length > value[0]){
            value[1] = value[1].slice(0, 4);
        }
    }

    year = timePadMap["year"][1].padStart(4, '0');
    month = timePadMap["month"][1].padStart(2, '0');
    day = timePadMap["day"][1].padStart(2, '0');
    hours = timePadMap["hours"][1].padStart(2, '0');
    minutes = timePadMap["minutes"][1].padStart(2, '0');
    seconds = timePadMap["seconds"][1].padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  }


  return (
    <div className="cdaWebDatetimeInputContainer">
      <div>
        <div className="cdaWebDatetimeInput">
          <label htmlFor="start-time">Start Time:  </label>
          <DateTimePicker
            format= "yyyy/MM/dd HH:mm:ss"
            id="start-time"
            name="start-time"
            value={safeParseTime(padDateTime(startTime))}
            clearIcon={null}
            isCalendarOpen={false}
            disableClock={true}
            calendarProps={{calendarType:"gregory"}}
            onChange={passToStartTimeChange}
          />
        </div>
        {timelineInfo && timelineInfo.start && (<div className="cdaWebModalTimelineInfo">
           <div>
             First Available Data:  {reformatDatetime(timelineInfo.start)}
           </div>
        </div>)}
      </div>

      <div>
        <div className="cdaWebDatetimeInput">
          <label htmlFor="stop-time">Stop Time: </label>
          <DateTimePicker
            format= "yyyy/MM/dd HH:mm:ss"
            id="stop-time"
            name="stop-time"
            value={safeParseTime(padDateTime(stopTime))}
            clearIcon={null}
            isCalendarOpen={false}
            disableClock={true}
            calendarProps={{calendarType:"gregory"}}
            onChange={passToStopTimeChange}
          />
        </div>
        {timelineInfo && timelineInfo.end && (<div className="cdaWebModalTimelineInfo">
          <div>
            Last Available Data:   {reformatDatetime(timelineInfo.end)}
          </div>
        </div>)}
      </div>
    </div>
  )
}


function reformatDatetime(datetimeStr) {
    let [datePart, timePart] = datetimeStr.split(' ');
    let [year, month, day] = datePart.split('/');
    let reformattedDate = `${year}/${month}/${day}`;
    let reformattedDatetime = `${reformattedDate} ${timePart.split('.')[0]} UTC`;

    return reformattedDatetime;
}


/**
 * Function to parse a Date, returning null if parsing fails
 *
 * @param timeString time string to parse
 * @return Date instance if parsing successful, null otherwise
 */
function safeParseTime(timeString) {
    try {
	return new Date(timeString)
    }
    catch (err) {
	return null;
    }
}


/**
 * Gets a datetime string in UTC.
 * @param String dateTime - The datetime string to format (in the format "YYYY-MM-DDTHH:mm:ss").
 * @returns String The UTC datetime string.
 */
const getUTCTimeString = (dateTime)=> {
        let utcTime = new Date(dateTime.getTime() - dateTime.getTimezoneOffset() * 60000);
        let utcTimeString = `${utcTime.getUTCFullYear()}`+
                    `-${(utcTime.getUTCMonth()+1).toString().padStart(2, '0')}`+
                    `-${utcTime.getUTCDate().toString().padStart(2, '0')}T`+
                    `${utcTime.getUTCHours().toString().padStart(2, '0')}`+
                    `:${utcTime.getUTCMinutes().toString().padStart(2, '0')}`+
                    `:${utcTime.getUTCSeconds().toString().padStart(2, '0')}`;
    return utcTimeString;
}
